import { type Message, useAgent } from "@copilotkit/react-core/v2";
import Box from "@memorang/ui/components/Box";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { ScrollView, View } from "react-native";

import { MessageContainer } from "@memorang/ui/components/RedeemCopilot/MessageContainer";
import { memo, useEffect, useMemo, useRef } from "react";
import { APPS_REDEEM_AGENT_NAME } from "../../constants";
import { ActionRenderer } from "../ActionRenderer";

const MemoizedMessageContainer = memo(MessageContainer);
const MemoizedActionRenderer = memo(ActionRenderer);

type RenderableItem =
	| {
			kind: "message";
			key: string;
			id: string;
			role: "user" | "assistant";
			content: string;
	  }
	| {
			kind: "action";
			key: string;
			actionName: string;
			result: string;
	  };

const toTextItem = (message: Message): RenderableItem | null => {
	if (message.role !== "user" && message.role !== "assistant") {
		return null;
	}
	if (typeof message.content !== "string" || !message.content) {
		return null;
	}
	return {
		kind: "message",
		key: message.id,
		id: message.id,
		role: message.role === "user" ? "user" : "assistant",
		content: message.content,
	};
};

const toActionItems = (
	message: Message,
	messages: Message[],
): RenderableItem[] => {
	if (message.role !== "assistant" || !message.toolCalls?.length) {
		return [];
	}
	const items: RenderableItem[] = [];
	for (const toolCall of message.toolCalls) {
		const toolMessage = messages.find(
			(m) => m.role === "tool" && m.toolCallId === toolCall.id,
		);
		if (toolMessage?.role === "tool") {
			items.push({
				kind: "action",
				key: `${message.id}-${toolCall.id}`,
				actionName: toolCall.function.name,
				result: toolMessage.content,
			});
		}
	}
	return items;
};

const buildRenderableItems = (messages: Message[]): RenderableItem[] =>
	messages.flatMap((message) => {
		const textItem = toTextItem(message);
		return textItem ? [textItem] : toActionItems(message, messages);
	});

export const Messages = () => {
	const theme = useAppTheme();
	const { agent } = useAgent({ agentId: APPS_REDEEM_AGENT_NAME });

	const messages = (agent.messages ?? []) as Message[];

	const renderableItems = useMemo<RenderableItem[]>(
		() => buildRenderableItems(messages),
		[messages],
	);

	const scrollViewRef = useRef<ScrollView | null>(null);
	const endRef = useRef<View | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: This is a valid useEffect
	useEffect(() => {
		if (scrollViewRef.current) {
			scrollViewRef.current.scrollToEnd({ animated: false });
		}
	}, [renderableItems]);

	return (
		<ScrollView
			ref={scrollViewRef}
			showsVerticalScrollIndicator={false}
			focusable
		>
			<Box flex={1} backgroundColor={theme.colors.surface} gap={16} padding={8}>
				{renderableItems.map((item) => {
					if (item.kind === "message") {
						return (
							<MemoizedMessageContainer
								key={item.key}
								message={{
									id: item.id,
									role: item.role,
									content: item.content,
								}}
							/>
						);
					}
					return (
						<MemoizedActionRenderer
							key={item.key}
							actionName={item.actionName}
							result={item.result}
						/>
					);
				})}
				<View style={{ height: 16 }} ref={endRef} />
			</Box>
		</ScrollView>
	);
};
